import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CART_ROUTE, CUSTOMER_PROFILE_ROUTE } from "@/utils/constants.js";
import { CUSTOMER_PROFILE_MENU, ACCOUNT_MENU_CART, ACCOUNT_MENU_LOGIN, ACCOUNT_MENU_REGISTER } from "@/utils/profileMenus.js";
import MenuItemLabel from "@/components/MenuItemLabel.jsx";
import { useViewportAnchoredMenu, canHoverFinePointer } from "@/hooks/useViewportAnchoredMenu.js";

export default function GuestAccountMenu({ onLogin, onRegister }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [open, setOpen] = useState(false);
    const closeTimerRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);

    const activeSection = searchParams.get("section");
    const onProfilePage = location.pathname === CUSTOMER_PROFILE_ROUTE
        || location.pathname.startsWith(`${CUSTOMER_PROFILE_ROUTE}/`);
    const onCartPage = location.pathname === CART_ROUTE
        || location.pathname.startsWith(`${CART_ROUTE}/`);

    useViewportAnchoredMenu(open, triggerRef, dropdownRef, { preferAlign: "end" });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const cancelClose = () => {
        if (closeTimerRef.current) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 180);
    };

    const openMenu = () => {
        cancelClose();
        setOpen(true);
    };

    const handleMouseEnter = () => {
        if (canHoverFinePointer()) {
            openMenu();
        }
    };

    const handleMouseLeave = () => {
        if (canHoverFinePointer()) {
            scheduleClose();
        }
    };

    const toggleMenu = () => {
        setOpen((current) => !current);
    };

    const goToSection = (sectionId) => {
        setOpen(false);
        navigate(`${CUSTOMER_PROFILE_ROUTE}?section=${sectionId}`);
    };

    const goToCart = () => {
        setOpen(false);
        navigate(CART_ROUTE);
    };

    const handleLogin = () => {
        setOpen(false);
        onLogin();
    };

    const handleRegister = () => {
        setOpen(false);
        onRegister();
    };

    return (
        <div
            ref={menuRef}
            className={`user-profile-menu${open ? " is-open" : ""}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                ref={triggerRef}
                type="button"
                className="user-profile-trigger"
                onClick={toggleMenu}
                aria-expanded={open}
                aria-haspopup="true"
            >
                <span className="user-profile-avatar" aria-hidden="true">?</span>
                <span className="user-profile-name">Compte</span>
                <span className="user-profile-chevron" aria-hidden="true" />
            </button>

            <div
                ref={dropdownRef}
                className="user-profile-dropdown"
                onMouseEnter={canHoverFinePointer() ? cancelClose : undefined}
            >
                <button
                    type="button"
                    className={`user-profile-dropdown-item${onCartPage ? " is-active" : ""}`}
                    onClick={goToCart}
                    title="Mon panier"
                    aria-current={onCartPage ? "page" : undefined}
                    data-tour="account-menu-cart"
                >
                    <MenuItemLabel icon={ACCOUNT_MENU_CART.icon}>Mon panier</MenuItemLabel>
                </button>
                {CUSTOMER_PROFILE_MENU.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className={`user-profile-dropdown-item${
                            onProfilePage && activeSection === item.id ? " is-active" : ""
                        }`}
                        onClick={() => goToSection(item.id)}
                        title={item.label}
                        aria-current={onProfilePage && activeSection === item.id ? "page" : undefined}
                    >
                        <MenuItemLabel icon={item.icon}>{item.label}</MenuItemLabel>
                    </button>
                ))}
                <div className="user-profile-dropdown-divider" />
                <button type="button" className="user-profile-dropdown-item" onClick={handleLogin}>
                    <MenuItemLabel icon={ACCOUNT_MENU_LOGIN.icon}>{ACCOUNT_MENU_LOGIN.label}</MenuItemLabel>
                </button>
                <button type="button" className="user-profile-dropdown-item" onClick={handleRegister}>
                    <MenuItemLabel icon={ACCOUNT_MENU_REGISTER.icon}>{ACCOUNT_MENU_REGISTER.label}</MenuItemLabel>
                </button>
            </div>
        </div>
    );
}
